import type { Schemas } from '@npc/api-client'
import { useMemo, useState } from 'react'

import { cn } from '../../components/cn'
import { t } from '../../copy'
import { T } from '../../copy/text'

/**
 * 「能碰哪些项目」那一格。**邀请表与改身份表共用这一份。**
 *
 * ------------------------------------------------------------------
 * # 它替换掉的东西
 *
 * 原来两张表各自把 `projects` 直接铺成一串复选框。项目二十多个的时候
 * （arron 2026-09-06 的截图里是 24 个，其中十来个是 `smoke-import-…`、
 * `任务C 撤销收口样本` 这样的验收残留），那一格要滚三屏，
 * 而他要找的那一个只能靠肉眼扫。
 *
 * 更糟的是老板那一档：`disabled={!needsProjects}` 把二十多个框全部灰着
 * 摆在那儿。这一屏的包注释自己写着——
 *
 * > 「这一列里不许有任何东西被灰掉——灰掉会读成『他没权限看这个项目』，
 * > 那是错的，而且错得刚好反过来。」
 *
 * 那条规矩当时只用在了名单那一列上，没用到这张表里。**同一条规矩只用在
 * 了一半的地方**（CLAUDE.md §8）。所以这里不再灰任何东西：老板那一档
 * 整块换成一句话，说的是同一个事实，而且没有一个能被误读的控件。
 *
 * # 顺序只在打开的那一刻定一次
 *
 * 已经给过的项目排在前面——改一个老同事时，「他现在有什么」一眼就看得到。
 * 但这个顺序**只在挂载时算一次**：跟着 `picked` 变的话，勾一下框那一行
 * 就会跳到别处去，而人的手还停在原来的位置上。
 * ------------------------------------------------------------------
 */
export function ProjectPicker({
  projects,
  picked,
  onChange,
  /** 老板那一档：看得见所有项目，不用挑，也没什么可挑的。 */
  ownerSeesAll,
  /** 复选框 data-testid 的前缀，两张表各用各的（`team-invite` / `team-member`）。 */
  testIdPrefix,
  legendId,
  hintId,
}: {
  projects: readonly Schemas['Project'][]
  picked: readonly string[]
  onChange: (next: readonly string[]) => void
  ownerSeesAll: boolean
  testIdPrefix: string
  /** 两张表各有各的标题模板 id（文字一样，但它们是两条独立的登记）。 */
  legendId: 'team.invite.form.projects' | 'team.member.dialog.projects'
  hintId: 'team.invite.form.projects_hint' | 'team.member.dialog.projects_hint'
}) {
  const [query, setQuery] = useState('')

  // 打开那一刻的选中集合，只用来定顺序。见上面「顺序只在打开的那一刻定一次」。
  const [initiallyPicked] = useState(() => new Set(picked))
  const ordered = useMemo(
    () =>
      [...projects].sort((a, b) => {
        const left = initiallyPicked.has(a.id) ? 0 : 1
        const right = initiallyPicked.has(b.id) ? 0 : 1
        return left - right
      }),
    [projects, initiallyPicked],
  )

  const needle = query.trim().toLowerCase()
  const shown = needle === '' ? ordered : ordered.filter((one) => one.name.toLowerCase().includes(needle))

  function setMany(ids: readonly string[], on: boolean) {
    const touched = new Set(ids)
    if (on) {
      onChange([...picked, ...ids.filter((id) => !picked.includes(id))])
      return
    }
    onChange(picked.filter((id) => !touched.has(id)))
  }

  return (
    <fieldset className="m-0 border-0 p-0" data-testid={`${testIdPrefix}-projects`}>
      <legend className="mb-[4px] text-npc-caption text-npc-ink-soft">
        <T id={legendId} />
      </legend>

      {ownerSeesAll ? (
        <p className="m-0 text-npc-body-sm text-npc-ink-soft" data-testid={`${testIdPrefix}-projects-owner`}>
          <T id="team.projects.owner_sees_all" />
        </p>
      ) : projects.length === 0 ? (
        <p className="m-0 text-npc-body-sm text-npc-ink-soft" data-testid={`${testIdPrefix}-projects-empty`}>
          <T id="team.projects.none_yet" />
        </p>
      ) : (
        <>
          <p className="m-0 mb-[8px] text-npc-meta text-npc-ink-softer">
            <T id={hintId} />
          </p>

          {/* 搜 + 两个批量按钮。窄屏（≤600）下换行，不挤成一条。 */}
          <div className="mb-[8px] flex flex-wrap items-center gap-npc-gap-sm">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('team.projects.search')}
              aria-label={t('team.projects.search')}
              data-testid={`${testIdPrefix}-projects-search`}
              className={cn(
                'min-w-[140px] flex-1 rounded-npc-button border border-npc-border-soft',
                'bg-npc-panel px-[10px] py-[6px] font-[inherit] text-npc-body-sm text-npc-ink',
                'placeholder:text-npc-ink-softer',
              )}
            />
            {/* 两个按钮动的是**当前列出来的那些**。搜着东西的时候这一点会被
                下面那行「挑了几个」当场说清楚，所以不必再写一句解释。 */}
            <button
              type="button"
              data-testid={`${testIdPrefix}-projects-all`}
              onClick={() => setMany(shown.map((one) => one.id), true)}
              className="rounded-npc-button border border-npc-border-soft px-[10px] py-[5px] text-npc-caption text-npc-ink-medium"
            >
              <T id="team.projects.pick_all" />
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-projects-none`}
              onClick={() => setMany(shown.map((one) => one.id), false)}
              className="rounded-npc-button border border-npc-border-soft px-[10px] py-[5px] text-npc-caption text-npc-ink-medium"
            >
              <T id="team.projects.pick_none" />
            </button>
          </div>

          {/* 列表自己滚，不把整张表撑到三屏高。 */}
          <div className="max-h-[210px] overflow-y-auto rounded-npc-inset border border-npc-border-soft">
            {shown.length === 0 ? (
              <p
                className="m-0 px-[10px] py-[10px] text-npc-body-sm text-npc-ink-soft"
                data-testid={`${testIdPrefix}-projects-no-match`}
              >
                {t('team.projects.no_match', { q: query.trim() })}
              </p>
            ) : (
              shown.map((project) => (
                <label
                  key={project.id}
                  className={cn(
                    'flex cursor-pointer items-center gap-[8px] border-b border-npc-border-softer',
                    'px-[10px] py-[7px] text-npc-body-sm last:border-b-0 hover:bg-npc-surface',
                  )}
                >
                  <input
                    type="checkbox"
                    data-testid={`${testIdPrefix}-project-${project.id}`}
                    checked={picked.includes(project.id)}
                    onChange={(event) => setMany([project.id], event.target.checked)}
                  />
                  <span className="min-w-0 truncate">{project.name}</span>
                </label>
              ))
            )}
          </div>

          <p
            className="m-0 mt-[6px] text-npc-meta text-npc-ink-softer"
            data-testid={`${testIdPrefix}-projects-count`}
          >
            {t('team.projects.picked', { picked: String(picked.length), total: String(projects.length) })}
          </p>
        </>
      )}
    </fieldset>
  )
}
